/**
 * Top outbound hosts by egress bytes across all audits in the 24h
 * window. Pulls from `infra.egress_by_host` in the `audit_cost_summary`
 * row.
 *
 * Empty-state is the rule at first — `egress_by_host` is not populated
 * in shipped data yet, per the ticket's "first-day-after-deploy will
 * have thin data" note. We render the empty card honestly so the slot
 * is reserved without faking zeros.
 */
import { ViewQueryButton } from "./ViewQueryButton";
import { formatBytes, formatInt } from "./format";
import type { ApiEnvelope, HotEgressData } from "./types";

interface HotEgressProps {
  envelope: ApiEnvelope<HotEgressData> | undefined;
  loading: boolean;
}

export function HotEgress({ envelope, loading }: HotEgressProps) {
  const rows = envelope?.data.rows ?? [];
  const empty = envelope?.data.empty_data ?? rows.length === 0;
  const max = Math.max(...rows.map((r) => r.total_bytes), 1);

  return (
    <section className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
            Hot egress hosts (24h)
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Top outbound hosts by egress bytes per audit.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="py-6 text-center text-sm text-zinc-500">loading…</div>
      ) : empty ? (
        <div className="py-6 text-center text-xs text-zinc-500">
          No per-host egress data yet — <code>infra.egress_by_host</code> is not populated in shipped data.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-zinc-500">
            <tr>
              <th className="text-left py-1">Host</th>
              <th className="text-right py-1">Bytes</th>
              <th className="text-right py-1">Audits</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 10).map((r) => {
              const pct = (r.total_bytes / max) * 100;
              return (
                <tr key={r.host} className="border-t border-zinc-100 dark:border-zinc-800">
                  <td className="py-1.5">
                    <div className="font-mono text-xs">{r.host}</div>
                    <div className="mt-1 h-1 rounded bg-zinc-100 dark:bg-zinc-800">
                      <div
                        className="h-full rounded bg-purple-500"
                        style={{ width: `${pct.toFixed(2)}%` }}
                      />
                    </div>
                  </td>
                  <td className="text-right text-xs font-semibold py-1.5 align-top">
                    {formatBytes(r.total_bytes)}
                  </td>
                  <td className="text-right text-xs text-zinc-500 py-1.5 align-top">
                    {formatInt(r.audit_count)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="mt-3">
        <ViewQueryButton query={envelope?.query} />
      </div>
    </section>
  );
}
